import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Icon from '../components/atoms/Icon';

const voiceOptions = [
  { value: 'female-1', label: 'Female - Expressive (US)', description: 'Breezy, expressive voice' },
  { value: 'female-2', label: 'Female - Authoritative (UK)', description: 'Firm, professional voice' },
  { value: 'male-1', label: 'Male - Energetic (US)', description: 'Upbeat, friendly voice' },
  { value: 'male-2', label: 'Male - Professional (UK)', description: 'Calm, professional voice' },
  { value: 'male-indian-1', label: 'Male - Indian Accent', description: 'Upbeat voice with Indian accent' },
];

const LiveInterviewDeviceCheckPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const micStreamRef = useRef(null);

  const [stream, setStream] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [micError, setMicError] = useState('');
  const [cameraDevices, setCameraDevices] = useState([]);
  const [micDevices, setMicDevices] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [selectedMic, setSelectedMic] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('female-1');
  const [isLoading, setIsLoading] = useState(true);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const faceDetectionRef = useRef(null);

  // Initialize devices
  useEffect(() => {
    const initializeDevices = async () => {
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        const devices = await navigator.mediaDevices.enumerateDevices();

        const cameras = devices.filter(d => d.kind === 'videoinput');
        const microphones = devices.filter(d => d.kind === 'audioinput');

        setCameraDevices(cameras);
        setMicDevices(microphones);

        if (cameras.length > 0) setSelectedCamera(cameras[0].deviceId);
        if (microphones.length > 0) setSelectedMic(microphones[0].deviceId);

        tempStream.getTracks().forEach(track => track.stop());
        setIsLoading(false);
      } catch (error) {
        console.error('Error initializing devices:', error);
        setIsLoading(false);
        setCameraError('Please allow camera and microphone access to continue.');
        setMicError('Please allow camera and microphone access to continue.');
      }
    };

    initializeDevices();

    return () => {
      cleanup();
    };
  }, []);

  // Start camera when camera device changes
  useEffect(() => {
    if (selectedCamera && !isLoading) {
      startCamera();
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (faceDetectionRef.current) {
        clearInterval(faceDetectionRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCamera]);

  // Start mic when mic device changes
  useEffect(() => {
    if (selectedMic && !isLoading) {
      startMic();
    }
    return () => {
      stopMic();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMic]);

  const startCamera = async () => {
    setCameraError('');
    setCameraReady(false);

    try {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (faceDetectionRef.current) {
        clearInterval(faceDetectionRef.current);
      }

      const constraints = {
        video: selectedCamera
          ? { deviceId: { exact: selectedCamera }, width: { ideal: 1280 }, height: { ideal: 720 } }
          : { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          setCameraReady(true);
          startFaceDetection();
        };
        try {
          await videoRef.current.play();
        } catch (e) {
          console.log('Autoplay prevented:', e);
        }
      }
    } catch (error) {
      console.error('Camera error:', error);
      if (error.name === 'NotAllowedError') {
        setCameraError('Camera access denied. Please allow access.');
      } else if (error.name === 'NotFoundError') {
        setCameraError('No camera found. Please connect a camera.');
      } else {
        setCameraError(`Camera error: ${error.message}`);
      }
    }
  };

  const startFaceDetection = () => {
    const interval = setInterval(() => {
      if (videoRef.current && videoRef.current.videoWidth > 0) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        ctx.drawImage(videoRef.current, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        let brightness = 0;
        for (let i = 0; i < data.length; i += 4) {
          brightness += (data[i] + data[i + 1] + data[i + 2]) / 3;
        }
        brightness = brightness / (data.length / 4);
        setFaceDetected(brightness > 50 && brightness < 200);
      }
    }, 1000);
    faceDetectionRef.current = interval;
  };

  const startMic = async () => {
    setMicError('');
    stopMic();

    try {
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: selectedMic ? { exact: selectedMic } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      micStreamRef.current = micStream;
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(micStream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;

      setIsListening(true);
      monitorAudioLevel();
    } catch (error) {
      console.error('Mic error:', error);
      if (error.name === 'NotAllowedError') {
        setMicError('Microphone access denied.');
      } else if (error.name === 'NotFoundError') {
        setMicError('No microphone found.');
      } else {
        setMicError(`Microphone error: ${error.message}`);
      }
    }
  };

  const stopMic = () => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    setIsListening(false);
    setAudioLevel(0);
  };

  const monitorAudioLevel = () => {
    if (!analyserRef.current) return;
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

    const check = () => {
      if (!analyserRef.current) return;
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      const normalized = Math.min(100, (average / 128) * 100);
      setAudioLevel(normalized);
      animationRef.current = requestAnimationFrame(check);
    };
    check();
  };

  const cleanup = () => {
    if (stream) stream.getTracks().forEach(track => track.stop());
    if (faceDetectionRef.current) clearInterval(faceDetectionRef.current);
    stopMic();
  };

  const handleContinue = () => {
    cleanup();
    navigate(`/live-interview/${location.state?.applicationId || 'manual-session'}`, {
      state: {
        ...location.state,
        cameraDeviceId: selectedCamera,
        micDeviceId: selectedMic,
        voice: selectedVoice
      }
    });
  };

  const handleBack = () => {
    cleanup();
    navigate(-1);
  };

  const canContinue = cameraReady && isListening && !cameraError && !micError;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header variant="default" />

      <main className="flex-1 px-4 py-8">
        <div className="max-w-5xl mx-auto">
          {/* Back + Header */}
          <button
            onClick={handleBack}
            className="flex items-center gap-1.5 text-gray-400 hover:text-gray-600 text-sm mb-6 transition-colors"
          >
            <Icon name="arrow_back" size={18} />
            <span>Back</span>
          </button>

          <div className="flex items-center gap-3 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold text-gray-900">Set Up Your Devices</h1>
                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 border border-emerald-100 rounded-full">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                  <span className="text-xs font-medium text-emerald-700">Live</span>
                </div>
              </div>
              <p className="text-gray-500 text-sm">Check your camera, microphone, and choose a voice before the live interview.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left - Camera Preview */}
            <div className="lg:col-span-7">
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <div className="relative aspect-video bg-gray-900 flex items-center justify-center">
                  {isLoading ? (
                    <div className="text-center">
                      <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                      <p className="text-gray-400 text-sm">Loading camera...</p>
                    </div>
                  ) : cameraError ? (
                    <div className="text-center px-6">
                      <Icon name="videocam_off" size={40} className="text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-400 text-sm mb-3">{cameraError}</p>
                      <button
                        onClick={startCamera}
                        className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg transition-colors"
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                      />

                      {/* Face guide overlay */}
                      <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-44 h-56 border-2 border-dashed border-white/30 rounded-2xl"></div>
                      </div>
                    </>
                  )}

                  {/* Status badges */}
                  <div className="absolute top-3 left-3 flex gap-2">
                    {cameraReady && !cameraError && (
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-black/50 backdrop-blur-sm rounded-lg">
                        <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full"></div>
                        <span className="text-white text-xs font-medium">Camera</span>
                      </div>
                    )}
                    {faceDetected && (
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-black/50 backdrop-blur-sm rounded-lg">
                        <Icon name="face" size={14} className="text-emerald-400" />
                        <span className="text-white text-xs font-medium">Face OK</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Camera selector */}
                <div className="p-4 border-t border-gray-100">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Camera</label>
                  <select
                    value={selectedCamera}
                    onChange={(e) => setSelectedCamera(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-[#0a1929] transition-colors"
                    disabled={cameraDevices.length === 0}
                  >
                    {cameraDevices.length === 0 ? (
                      <option>No camera detected</option>
                    ) : (
                      cameraDevices.map((device, i) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Camera ${i + 1}`}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>
            </div>

            {/* Right - Mic, Voice, Tips */}
            <div className="lg:col-span-5 space-y-4">
              {/* Microphone Card */}
              <div className="bg-white border border-gray-200 rounded-2xl p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 bg-[#0a1929] rounded-lg flex items-center justify-center">
                    <Icon name="mic" size={16} className="text-white" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900">Microphone</h3>
                  {isListening && (
                    <div className="ml-auto flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></div>
                      <span className="text-xs text-emerald-600 font-medium">Active</span>
                    </div>
                  )}
                </div>

                <select
                  value={selectedMic}
                  onChange={(e) => setSelectedMic(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-[#0a1929] mb-4 transition-colors"
                  disabled={micDevices.length === 0}
                >
                  {micDevices.length === 0 ? (
                    <option>No microphone detected</option>
                  ) : (
                    micDevices.map((device, i) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Microphone ${i + 1}`}
                      </option>
                    ))
                  )}
                </select>

                {micError ? (
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-3">
                    <p className="text-red-600 text-xs">{micError}</p>
                    <button onClick={startMic} className="text-red-700 text-xs font-medium mt-1 hover:underline">Retry</button>
                  </div>
                ) : (
                  <>
                    {/* Audio level bar */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-gray-500">Audio Level</span>
                        <span className="text-xs text-gray-400">{Math.round(audioLevel)}%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-100 ${
                            audioLevel > 30 ? 'bg-emerald-500' : audioLevel > 10 ? 'bg-amber-400' : 'bg-gray-300'
                          }`}
                          style={{ width: `${audioLevel}%` }}
                        />
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                      <p className="text-xs text-gray-600 text-center">
                        Say: <span className="font-medium text-gray-900">"Hello, I'm ready for my live interview"</span>
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Voice Selection Card */}
              <div className="bg-white border border-gray-200 rounded-2xl p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 bg-[#0a1929] rounded-lg flex items-center justify-center">
                    <Icon name="record_voice_over" size={16} className="text-white" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900">Interviewer Voice</h3>
                </div>

                <select
                  value={selectedVoice}
                  onChange={(e) => setSelectedVoice(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-[#0a1929] transition-colors"
                >
                  {voiceOptions.map((voice) => (
                    <option key={voice.value} value={voice.value}>
                      {voice.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-2">
                  {voiceOptions.find(v => v.value === selectedVoice)?.description}
                </p>
              </div>

              {/* Tips Card */}
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
                <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Quick Tips</h3>
                <div className="space-y-2.5">
                  {[
                    'Position your face in the center guide',
                    'Ensure good lighting on your face',
                    'Speak at normal conversational volume',
                    'Stay within the frame during the interview',
                  ].map((tip, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Icon name="check" size={14} className="text-[#0a1929] mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-gray-600">{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Continue Button */}
          <div className="mt-8 flex justify-end">
            <button
              onClick={handleContinue}
              disabled={!canContinue}
              className="px-8 py-3.5 bg-[#0a1929] hover:bg-[#0d2137] text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              Start Live Interview
              <Icon name="arrow_forward" size={18} className="text-white" />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LiveInterviewDeviceCheckPage;
